import React from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import AppLayout from "./components/AppLayout";
import HomePage from "./pages/Homepage";
import SchedulePage from "./pages/SchedulePage";
import CourtAllocation from "./pages/CourtAllocation";
import BKOverview from "./pages/Bk_overview";
import Freikarten from "./pages/Freikarten";
import BallkindPage from "./pages/BallkindPage";
import BallkinderEinteilung from "./pages/Balkinder_Einteilung";
import { BallkidProvider } from "./context/BallkidContext";
import { TournamentConfigProvider } from "./context/TournamentConfigContext";
import { GroupProvider, useGroup } from "./context/GroupContext";
import ProtectedRoute from "./ProtectedRoute";
import Login from "./Login";
import Report from "./pages/AttendanceReport";
import Comment from "./pages/BallkidCommentsPage";
import Essensmarken from "./pages/MealTicketAllocation";
import AttendanceBilling from "./pages/AttendanceBilling";
import CrewSchedule from "./pages/CourtCrewTimes";
import ClothingDistribution from "./pages/ClothingDistribution";
import CourtAssignmentsPrintPage from "./pages/CourtAssignmentsPrintPage";
import NotFoundPage from "./pages/NotFoundPage";
import BallkidPageSettings from "./pages/BallkidPageSettings";
import CourtStartTimesSettings from "./pages/CourtStartTimesSettings";
import AdminPage from "./pages/AdminPage";
import DocumentationPage from "./pages/DocumentationPage";
import PollManagementPage from "./pages/PollManagementPage";
import PollResultsPage from "./pages/PollResultsPage";
import PollVotePage from "./pages/PollVotePage";
import PublicCrewTimesPage from "./pages/PublicCrewTimesPage";
import CheckInScanner from "./pages/CheckInScanner";
import CheckInTestPage from "./pages/CheckInTestPage";

// Redirect / to /:activeGroupSlug
const RootRedirect = () => {
  const { activeGroup, groupsLoading } = useGroup();
  if (groupsLoading) return null;
  return <Navigate to={`/${activeGroup?.slug || "ballkinder"}`} replace />;
};

function App() {
  return (
    <GroupProvider>
    <TournamentConfigProvider>
    <BallkidProvider>
      <Router>
        <Routes>
          {/* Public routes - no sidebar */}
          <Route path="/login" element={<Login />} />
          <Route path="/:groupSlug/member/:id" element={<BallkindPage />} />
          <Route path="/:groupSlug/crew-times" element={<PublicCrewTimesPage />} />
          <Route path="/:groupSlug/poll/:pollId/:id" element={<PollVotePage />} />
          {/* Legacy routes for backwards compatibility */}
          <Route path="/ballkind/:id" element={<BallkindPage />} />
          <Route path="/poll/:pollId/:id" element={<PollVotePage />} />

          {/* Root redirect to active group */}
          <Route path="/" element={<ProtectedRoute><RootRedirect /></ProtectedRoute>} />

          {/* Protected routes with group slug and sidebar layout */}
          <Route path="/:groupSlug" element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
            <Route index element={<HomePage />} />
            <Route path="schedule" element={<SchedulePage />} />
            <Route path="court-allocation" element={<CourtAllocation />} />
            <Route path="overview" element={<BKOverview />} />
            <Route path="freikarten" element={<Freikarten />} />
            <Route path="einteilung" element={<BallkinderEinteilung />} />
            <Route path="essensmarken" element={<Essensmarken />} />
            <Route path="report" element={<Report />} />
            <Route path="comments" element={<Comment />} />
            <Route path="attendance-billing" element={<AttendanceBilling />} />
            <Route path="court_crew_times" element={<CrewSchedule />} />
            <Route path="clothing_distribution" element={<ClothingDistribution />} />
            <Route path="all_assignments" element={<CourtAssignmentsPrintPage />} />
            <Route path="polls" element={<PollManagementPage />} />
            <Route path="poll-results/:pollId" element={<PollResultsPage />} />
            <Route path="ballkid-page-settings" element={<BallkidPageSettings />} />
            <Route path="court-start-times" element={<CourtStartTimesSettings />} />
            <Route path="checkin-scanner" element={<CheckInScanner />} />
            <Route path="admin" element={<AdminPage />} />
            <Route path="admin/checkin-test" element={<CheckInTestPage />} />
            <Route path="docs" element={<DocumentationPage />} />
          </Route>

          {/* Legacy redirects - old URLs without group slug */}
          <Route path="/schedule" element={<LegacyRedirect page="schedule" />} />
          <Route path="/court-allocation" element={<LegacyRedirect page="court-allocation" />} />
          <Route path="/overview" element={<LegacyRedirect page="overview" />} />
          <Route path="/freikarten" element={<LegacyRedirect page="freikarten" />} />
          <Route path="/einteilung" element={<LegacyRedirect page="einteilung" />} />
          <Route path="/essensmarken" element={<LegacyRedirect page="essensmarken" />} />
          <Route path="/report" element={<LegacyRedirect page="report" />} />
          <Route path="/comments" element={<LegacyRedirect page="comments" />} />
          <Route path="/attendance-billing" element={<LegacyRedirect page="attendance-billing" />} />
          <Route path="/court_crew_times" element={<LegacyRedirect page="court_crew_times" />} />
          <Route path="/clothing_distribution" element={<LegacyRedirect page="clothing_distribution" />} />
          <Route path="/all_assignments" element={<LegacyRedirect page="all_assignments" />} />
          <Route path="/polls" element={<LegacyRedirect page="polls" />} />
          <Route path="/poll-results/:pollId" element={<LegacyRedirect page="poll-results" />} />
          <Route path="/ballkid-page-settings" element={<LegacyRedirect page="ballkid-page-settings" />} />
          <Route path="/court-start-times" element={<LegacyRedirect page="court-start-times" />} />
          <Route path="/checkin-scanner" element={<LegacyRedirect page="checkin-scanner" />} />
          <Route path="/admin" element={<LegacyRedirect page="admin" />} />
          <Route path="/docs" element={<LegacyRedirect page="docs" />} />

          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Router>
    </BallkidProvider>
    </TournamentConfigProvider>
    </GroupProvider>
  );
}

// Redirect old URLs (e.g. /freikarten) to new format (e.g. /ballkinder/freikarten)
const LegacyRedirect = ({ page }) => {
  const { activeGroup, groupsLoading } = useGroup();
  if (groupsLoading) return null;
  return <Navigate to={`/${activeGroup?.slug || "ballkinder"}/${page}`} replace />;
};

export default App;
